import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import { NavigationGlyph, PageHeading } from '@traceio-org/ui';
import { AdminPage } from '../../components/AdminPage';
import { ActionLink, AdminProblem } from '../../components/AdminProblem';
import { SavedNotice, useNow } from '../../components/feedback';
import { ReasonField, reasonError } from '../../components/fields';
import { StatusBadge } from '../../components/records';
import { SupportAccessBanner } from '../../components/SupportAccessBanner';
import { WriteDialog } from '../../components/WriteDialog';
import { useAbilities, useAdmin, useStaff } from '../../context';
import type { StaffAbilities } from '../../permissions';
import { useAdminRead } from '../../useAdminRead';
import { createdNoticeOf } from '../NewOrganizationDialog';
import type { ListReturnState } from '../OrganizationsPage';
import { OrganizationScopeProvider, isGrantActive, type OrganizationScope } from './scope';

// Organization ids are UUIDs (platform-api answers 400 to anything else); other addresses read nothing.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The sections of an organization, each its own address under /organizations/:id. A section with `requires` is
 * shown only to staff with that ability (Billing: staff.billing.read); its page refuses a deep link on its own.
 */
export const ORGANIZATION_TABS: readonly {
  id: string;
  label: string;
  path: string;
  requires?: keyof StaffAbilities;
}[] = [
  { id: 'overview', label: 'Overview', path: '' },
  { id: 'modules', label: 'Modules', path: 'modules' },
  { id: 'entitlements', label: 'Entitlements', path: 'entitlements' },
  { id: 'billing', label: 'Billing', path: 'billing', requires: 'readBilling' },
  { id: 'members', label: 'Members', path: 'members' },
  { id: 'invitations', label: 'Invitations', path: 'invitations' },
  { id: 'domains', label: 'Domains', path: 'domains' },
  { id: 'portals', label: 'Customer portals', path: 'portals' },
  { id: 'support-access', label: 'Support access', path: 'support-access' },
  { id: 'tickets', label: 'Tickets', path: 'tickets', requires: 'readTickets' },
  { id: 'audit', label: 'Audit', path: 'audit' },
];

type OrganizationTab = (typeof ORGANIZATION_TABS)[number];

/** The list view the person came from (its search and page), or the first page. */
export function listLocation(state: unknown): { pathname: '/organizations'; search: string } {
  const search = (state as Partial<ListReturnState> | null)?.listSearch;
  // Only a query string goes back into the link: the path is always the list's own.
  return {
    pathname: '/organizations',
    search: typeof search === 'string' && search.startsWith('?') ? search : '',
  };
}

/** The part of the history state that survives moving between tabs: the way back to the list. */
export function carriedState(state: unknown): ListReturnState | null {
  const search = (state as Partial<ListReturnState> | null)?.listSearch;
  return typeof search === 'string' ? { listSearch: search } : null;
}

function tabOf(pathname: string, base: string): OrganizationTab {
  const rest = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\/+/, '') : '';
  const first = rest.split('/')[0] ?? '';
  return ORGANIZATION_TABS.find((tab) => tab.path === first) ?? ORGANIZATION_TABS[0]!;
}

/** Staff reads of customer organizations are audited by platform-api; the page says so up front. */
function AuditNote() {
  return (
    <div className="admin-audit-note" role="note">
      <span className="admin-audit-note__icon" aria-hidden="true">
        <NavigationGlyph name="ShieldCheck" />
      </span>
      <p>
        <strong>Staff reads are audited.</strong> Opening a customer organization records the read with your
        name and staff role.
      </p>
    </div>
  );
}

/**
 * The sections of the organization as links (each one an address that survives a reload and the back button),
 * marked with aria-current. The library's PageTabs is a client-side tablist without panels, so the app keeps
 * its own navigation here, in the library's tab look (admin.css).
 */
function OrganizationTabs({
  active,
  base,
  state,
  abilities,
}: {
  active: OrganizationTab;
  base: string;
  state: ListReturnState | null;
  abilities: StaffAbilities;
}) {
  // On a narrow screen the links scroll sideways; the current one is brought into view (sideways only, so
  // the page itself never jumps).
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const nav = ref.current;
    const link = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !link || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = Math.max(0, link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2);
  }, [active.id]);
  return (
    <nav ref={ref} className="admin-tabs" aria-label="Organization sections">
      <ul>
        {ORGANIZATION_TABS.filter((tab) => tab.requires === undefined || abilities[tab.requires]).map(
          (tab) => (
            <li key={tab.id}>
              <Link
                to={tab.path ? `${base}/${tab.path}` : base}
                state={state}
                aria-current={tab.id === active.id ? 'page' : undefined}
              >
                {tab.label}
              </Link>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

/**
 * An organization (/organizations/:id and its sections): GET /v1/admin/organizations/:id for the heading,
 * the caller's support access grants for the banner, then the section the address names. The page keeps one
 * h1 (the organization's name) in every state and on every tab; moving between tabs keeps focus on the tab
 * link instead of moving it to the heading.
 */
export function OrganizationLayout() {
  const { organizationId = '' } = useParams();
  const location = useLocation();
  const { api } = useAdmin();
  const { person } = useStaff();
  const abilities = useAbilities();
  // The tick re-renders the banner (time left, the end of the grant); each decision reads the clock afresh, so a
  // grant that just started is never judged against a time cached before it.
  const tick = useNow(15_000);
  const valid = UUID.test(organizationId);
  const base = `/organizations/${organizationId}`;
  const tab = tabOf(location.pathname, base);
  const state = carriedState(location.state);
  const back = listLocation(location.state);
  // Arriving from New organization: what it did. Moving between tabs carries only the way back, so it goes away.
  const createdNotice = createdNoticeOf(location.state);

  const key = valid ? `organization\u0000${organizationId}` : null;
  const read = useAdminRead(key, (signal) => api.getOrganization(organizationId, signal));
  // Only the answer for this very organization: going back and forth between two organizations keeps this
  // component mounted, and the previous one must never show under the next one's address.
  const organization = read.dataKey === key && !read.failure ? read.data : null;
  const failure = valid ? read.failure : { kind: 'not-found' as const };
  const notFound = failure?.kind === 'not-found' || failure?.kind === 'invalid-request';

  const grantsKey = valid ? `grants\u0000${organizationId}` : null;
  const grants = useAdminRead(grantsKey, (signal) =>
    api.listSupportAccessGrants({ organizationId, status: 'all' }, signal),
  );
  const activeGrant = useMemo(() => {
    if (grants.dataKey !== grantsKey || !grants.data) return null;
    const at = Math.max(tick, Date.now());
    return (
      grants.data.items.find(
        (grant) =>
          grant.organizationId === organizationId &&
          grant.staffUserId === person.userId &&
          isGrantActive(grant, at),
      ) ?? null
    );
  }, [grants.data, grants.dataKey, grantsKey, organizationId, person.userId, tick]);
  const now = Math.max(tick, Date.now());

  const [ending, setEnding] = useState(false);
  const [endReason, setEndReason] = useState('');
  const [endError, setEndError] = useState<string | null>(null);

  const scope: OrganizationScope | null = organization
    ? {
        organization,
        abilities,
        staffUserId: person.userId,
        grants,
        activeGrant,
        mayNeedSupportAccess: !abilities.readsWithoutGrant && activeGrant === null,
        tabPath: (path) => (path ? `${base}/${path}` : base),
        reloadOrganization: read.retry,
      }
    : null;

  let title = 'Organization';
  if (organization) title = organization.displayName;
  else if (notFound) title = 'Organization not found';
  const documentTitle = organization && tab.id !== 'overview' ? `${tab.label} | ${title}` : title;

  let description: ReactNode = null;
  if (organization) {
    description = (
      <span className="admin-heading-meta">
        <code>{organization.slug}</code>
        <StatusBadge status={organization.status} />
        {organization.billingExempt ? <StatusBadge status="billing_exempt" /> : null}
      </span>
    );
  } else if (notFound) {
    description = 'No organization has this address. It may have been removed, or the link is wrong.';
  }

  return (
    <AdminPage title={documentTitle}>
      <Link className="admin-back" to={back}>
        <span aria-hidden="true">&larr;</span> Organizations
      </Link>
      <PageHeading eyebrow={organization ? 'Organization' : null} title={title} description={description} />
      {createdNotice ? <SavedNotice message={createdNotice} /> : null}
      <AuditNote />
      {organization && activeGrant ? (
        <SupportAccessBanner
          grant={activeGrant}
          organizationName={organization.displayName}
          now={now}
          onEnd={() => {
            setEndReason('');
            setEndError(null);
            setEnding(true);
          }}
        />
      ) : null}
      {scope ? (
        <>
          <OrganizationTabs active={tab} base={base} state={state} abilities={abilities} />
          <OrganizationScopeProvider value={scope}>
            <div className="admin-tab-panel">
              <Outlet />
            </div>
          </OrganizationScopeProvider>
        </>
      ) : null}
      {!organization && failure && notFound ? (
        <div className="admin-problem-actions">
          <ActionLink primary to={back}>
            Back to organizations
          </ActionLink>
        </div>
      ) : null}
      {!organization && failure && !notFound ? (
        <AdminProblem failure={failure} subject="this organization" onRetry={read.retry} />
      ) : null}
      {!organization && !failure ? (
        <div className="admin-loading" role="status">
          <span className="admin-spinner" aria-hidden="true" />
          Loading the organization
        </div>
      ) : null}
      {activeGrant && organization ? (
        <WriteDialog
          open={ending}
          onClose={() => setEnding(false)}
          title="End support access now?"
          description={`You stop reading ${organization.displayName}'s data at once.`}
          submitLabel="End support access"
          onSubmit={async () => {
            const error = endReason.trim() ? reasonError(endReason) : null;
            setEndError(error);
            if (error) return 'invalid';
            await api.revokeSupportAccess(activeGrant.id, endReason);
            return undefined;
          }}
          onSaved={() => grants.retry()}
        >
          <ReasonField optional value={endReason} onChange={setEndReason} error={endError} />
        </WriteDialog>
      ) : null}
    </AdminPage>
  );
}
